import { Input, Button } from '@material-tailwind/react'
import { useEffect, useState } from 'react'
import { LockClosedIcon } from '@heroicons/react/solid'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { H3, P } from '../../components/typography/typography'
import userService from '../../Services/user.service'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

interface ChangePasswordForm {
  currentPass: string
  pass: string
  confirmPass: string
}

export default function Settings() {
  const [errorMessage, setErrorMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  let schema = yup.object().shape({
    currentPass: yup.string().required('Current password is required'),
    pass: yup.string().required('New password is required').min(6, 'Must contain at least 6 characters'),
    confirmPass: yup
      .string()
      .required('Confirm new password is required')
      .oneOf([yup.ref('pass'), null], 'Password and Confirm Password must match'),
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<ChangePasswordForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const watchFields = watch()

  useEffect(() => {
    setErrorMessage('')
  }, [watchFields])

  async function onSubmit(values: ChangePasswordForm) {
    setIsSaving(true)
    try {
      const payLoad = JSON.stringify({ currentPassword: values.currentPass, newPassword: values.pass })

      await userService.ChangePassword(payLoad)

      // The back end leaves this session signed in, so the user stays on the page.
      reset({ currentPass: '', pass: '', confirmPass: '' })
      toast.success('Your password has been changed.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === 'string' ? error.response.data : '') ||
        'Password change failed, please try again'

      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT,
      })
      setErrorMessage(message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <section>
      <div className="w-full mb-10">
        <H3 className="mb-2">Settings</H3>
        <P className="text-bluish-grey">Manage your account.</P>
      </div>
      <div className="bg-white p-8 rounded-lg max-w-xl">
        <H3 className="text-xl mb-2">Change Password</H3>
        <P className="text-bluish-grey mb-6">Enter your current password and choose a new one.</P>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4">
            <Input
              label="Current Password"
              type="password"
              className="mb-5"
              icon={<LockClosedIcon className="text-blue-600" />}
              tabIndex={1}
              {...register('currentPass')}
            />
            <Input
              label="New Password"
              type="password"
              className="mb-5"
              icon={<LockClosedIcon className="text-blue-600" />}
              tabIndex={2}
              {...register('pass')}
            />
            <Input
              label="Confirm New Password"
              type="password"
              className="mb-5"
              icon={<LockClosedIcon className="text-blue-600" />}
              tabIndex={3}
              {...register('confirmPass')}
            />
            <Button type="submit" tabIndex={4} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Change Password'}
            </Button>
            {errors.currentPass?.message && <P className="text-red-600">{errors.currentPass?.message}</P>}
            {errors.pass?.message && <P className="text-red-600">{errors.pass?.message}</P>}
            {errors.confirmPass?.message && <P className="text-red-600">{errors.confirmPass?.message}</P>}
            {errorMessage && <P className="text-red-600">{errorMessage}</P>}
          </div>
          <ToastContainer />
        </form>
      </div>
    </section>
  )
}
